import { clashKey, type ClashResult } from '@viewer/clash/lib'
import { conflitosAExportar, type ClashTopicDraft } from './clashBcf'
import { dataCurtaPt, resultadosResolvidosAusentes } from './clashReconciliacao'
import {
  isTriageState,
  TRIAGE_LABELS,
  triageOf,
  type ClashTriageMap,
  type ClashTriageState,
} from './clashTriage'

// ---------------------------------------------------------------------------
// Registo da ultima exportacao para ficheiro e "So o que mudou desde a
// ultima exportacao".
//
// A cada exportacao para ficheiro (.bcfzip) guarda-se, por conflito
// (clashKey), o estado com que saiu, o Guid do topico em que saiu e o
// grupo indicado. O registo ACUMULA: uma exportacao parcial junta-se ao
// que ja la estava, porque a plataforma que recebeu os ficheiros continua
// a ter os topicos das exportacoes anteriores. Fica no ficheiro das
// corridas (clashTriageStorage.ts) e, por isso, na sessao .vn2r-session
// (seccao conflitos, campo opcional: as sessoes v7 sem ele leem-se sem
// registo).
//
// A exportacao parcial leva:
//   - os conflitos NOVOS: os que o criterio em uso exportaria (marcados,
//     por resolver, so com triagem) e que nunca foram exportados;
//   - os ALTERADOS: exportados antes com outro estado (resolvidos,
//     reabertos, aceites), tambem os resolvidos que ja nao ocorrem
//     (reconstruidos da ultima geometria conhecida).
// Os Guids sao os estaveis de sempre (clashIds.ts): uma plataforma que
// junte por Guid actualiza os topicos que ja tem. Cada alterado leva um
// comentario com a mudanca de estado.
//
// RelatedTopics num ficheiro parcial: um conflito de um grupo aponta
// tambem para os topicos do mesmo grupo das exportacoes anteriores que
// nao vao neste ficheiro (os Guids estaveis do registo). Existem na
// plataforma que recebeu as exportacoes anteriores.
// ---------------------------------------------------------------------------

export interface ClashExportado {
  /** Estado da triagem com que o conflito saiu. */
  estado: ClashTriageState
  /** Guid do topico em que saiu. */
  topico: string
  /** Grupo indicado no topico (o id da triagem), quando havia. */
  grupo?: string
  /** Saiu num topico de grupo ("um topico por grupo"). */
  deGrupo?: boolean
}

export interface ClashUltimaExportacao {
  /** ISO da ultima exportacao para ficheiro. */
  data: string
  /** Por clashKey. */
  chaves: Record<string, ClashExportado>
}

/** Le o registo de um objecto ja lido (ficheiro ou sessao). Entrada
 *  estragada le-se entrada a entrada; sem nada aproveitavel, null. */
export function sanitizeUltimaExportacao(v: unknown): ClashUltimaExportacao | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (typeof o.data !== 'string' || !o.chaves || typeof o.chaves !== 'object') return null
  const chaves: Record<string, ClashExportado> = {}
  for (const [k, e] of Object.entries(o.chaves as Record<string, unknown>)) {
    if (!e || typeof e !== 'object') continue
    const x = e as Record<string, unknown>
    if (!isTriageState(x.estado) || typeof x.topico !== 'string' || !x.topico) continue
    chaves[k] = {
      estado: x.estado,
      topico: x.topico,
      ...(typeof x.grupo === 'string' && x.grupo ? { grupo: x.grupo } : {}),
      ...(x.deGrupo === true ? { deGrupo: true } : {}),
    }
  }
  return { data: o.data, chaves }
}

/**
 * O registo depois de uma exportacao: o anterior com os conflitos agora
 * exportados por cima (estado actual, Guid do topico, grupo). `guids` e o
 * Guid de cada rascunho, pela mesma ordem.
 */
export function registoDaExportacao(
  anterior: ClashUltimaExportacao | null,
  drafts: readonly Pick<ClashTopicDraft, 'key' | 'keys' | 'grupo'>[],
  guids: readonly string[],
  triage: ClashTriageMap,
  data: string,
): ClashUltimaExportacao {
  const chaves: Record<string, ClashExportado> = { ...(anterior?.chaves ?? {}) }
  drafts.forEach((d, i) => {
    const deGrupo = d.key.startsWith('grupo:')
    for (const k of d.keys) {
      chaves[k] = {
        estado: triageOf(triage, k).state,
        topico: guids[i],
        ...(d.grupo ? { grupo: d.grupo } : {}),
        ...(deGrupo ? { deGrupo: true } : {}),
      }
    }
  })
  return { data, chaves }
}

export interface DeltaDaExportacao {
  /** Nunca exportados, pelo criterio em uso. */
  novos: ClashResult[]
  /** Exportados antes com outro estado. */
  alterados: ClashResult[]
  /** O que sai no ficheiro: novos e alterados e, com "um topico por
   *  grupo", os outros conflitos dos grupos tocados (um topico de grupo
   *  sai sempre inteiro, para nao encolher na plataforma). */
  aExportar: ClashResult[]
}

/**
 * O que mudou desde a ultima exportacao. Com marcados, so entre eles.
 * Sem marcados: os novos sao os do criterio (por resolver, so com
 * triagem); os alterados podem ser quaisquer, tambem resolvidos e
 * resolvidos que ja nao ocorrem.
 */
export function deltaDesdeUltima(o: {
  results: readonly ClashResult[]
  triage: ClashTriageMap
  ultima: ClashUltimaExportacao
  checked?: ReadonlySet<string>
  soComTriagem?: boolean
  umPorGrupo?: boolean
}): DeltaDaExportacao {
  const marcados = o.checked && o.checked.size > 0 ? o.checked : null
  const presentes = new Set(o.results.map(clashKey))
  const universo: ClashResult[] = marcados
    ? o.results.filter((r) => marcados.has(clashKey(r)))
    : [...o.results, ...resultadosResolvidosAusentes(o.triage, presentes)]
  const criterio = new Set(
    conflitosAExportar(o.results, o.triage, { checked: o.checked, soComTriagem: o.soComTriagem }).map(clashKey),
  )
  const novos: ClashResult[] = []
  const alterados: ClashResult[] = []
  for (const r of universo) {
    const k = clashKey(r)
    const antes = o.ultima.chaves[k]
    if (!antes) {
      if (criterio.has(k)) novos.push(r)
      continue
    }
    if (antes.estado !== triageOf(o.triage, k).state) alterados.push(r)
  }
  let aExportar = [...novos, ...alterados]
  if (o.umPorGrupo) {
    const tocados = new Set(
      aExportar.map((r) => o.triage[clashKey(r)]?.group).filter((g): g is string => !!g),
    )
    const ja = new Set(aExportar.map(clashKey))
    const extra = universo.filter((r) => {
      const g = o.triage[clashKey(r)]?.group
      return !!g && tocados.has(g) && !ja.has(clashKey(r))
    })
    aExportar = [...aExportar, ...extra]
  }
  // Pela ordem do universo (a da lista), para o ficheiro sair estavel.
  const ordem = new Map(universo.map((r, i) => [clashKey(r), i]))
  aExportar.sort((a, b) => (ordem.get(clashKey(a)) ?? 0) - (ordem.get(clashKey(b)) ?? 0))
  return { novos, alterados, aExportar }
}

/** O comentario de um topico cujo estado mudou desde a ultima
 *  exportacao, ou null se nao mudou (ou nunca foi exportado). Num topico
 *  de grupo, conta os pares que mudaram. */
export function comentarioDaMudanca(
  keys: readonly string[],
  triage: ClashTriageMap,
  ultima: ClashUltimaExportacao,
): string | null {
  const mudancas: { de: ClashTriageState; para: ClashTriageState }[] = []
  for (const k of keys) {
    const antes = ultima.chaves[k]
    if (!antes) continue
    const agora = triageOf(triage, k).state
    if (antes.estado !== agora) mudancas.push({ de: antes.estado, para: agora })
  }
  if (mudancas.length === 0) return null
  const dia = dataCurtaPt(ultima.data)
  if (keys.length === 1) {
    const m = mudancas[0]
    return `Estado alterado desde a exportação de ${dia}: ${TRIAGE_LABELS[m.de]} para ${TRIAGE_LABELS[m.para]}.`
  }
  const porPara = new Map<ClashTriageState, number>()
  for (const m of mudancas) porPara.set(m.para, (porPara.get(m.para) ?? 0) + 1)
  const partes = [...porPara].map(([para, n]) => `${n} ${TRIAGE_LABELS[para].toLowerCase()}${n === 1 ? '' : 's'}`)
  return `Desde a exportação de ${dia}, ${mudancas.length} par${mudancas.length === 1 ? '' : 'es'} do grupo ${mudancas.length === 1 ? 'mudou' : 'mudaram'} de estado: ${partes.join(', ')}.`
}

/**
 * Os RelatedTopics a acrescentar num ficheiro parcial: para cada rascunho
 * de conflito com grupo, os topicos de conflito do mesmo grupo das
 * exportacoes anteriores que nao vao neste ficheiro. `guids` e o Guid de
 * cada rascunho.
 */
export function relacionadosAnteriores(
  drafts: readonly Pick<ClashTopicDraft, 'key' | 'grupo'>[],
  guids: readonly string[],
  ultima: ClashUltimaExportacao,
): string[][] {
  const nesteFicheiro = new Set(guids)
  const porGrupo = new Map<string, string[]>()
  for (const e of Object.values(ultima.chaves)) {
    if (!e.grupo || e.deGrupo || nesteFicheiro.has(e.topico)) continue
    const l = porGrupo.get(e.grupo) ?? []
    if (!l.includes(e.topico)) l.push(e.topico)
    porGrupo.set(e.grupo, l)
  }
  return drafts.map((d, i) => {
    if (!d.grupo || d.key.startsWith('grupo:')) return []
    return (porGrupo.get(d.grupo) ?? []).filter((g) => g !== guids[i])
  })
}

/** O registo com as chaves migradas por GlobalId (outra revisao do IFC,
 *  outros localId): o conflito continua a ser o mesmo. */
export function migrarChavesDaExportacao(
  ultima: ClashUltimaExportacao | null,
  migradas: Readonly<Record<string, string>>,
): ClashUltimaExportacao | null {
  if (!ultima || Object.keys(migradas).length === 0) return ultima
  const chaves: Record<string, ClashExportado> = {}
  for (const [k, e] of Object.entries(ultima.chaves)) chaves[migradas[k] ?? k] = e
  return { ...ultima, chaves }
}
